import { generateClient } from 'aws-amplify/api'
import {
  addOrgDefaultFields as addOrgDefaultFieldsMutation,
  removeOrgDefaultFields as removeOrgDefaultFieldsMutation,
  addOrgDefaultLeadStatuses as addOrgDefaultLeadStatusesMutation,
  removeOrgDefaultLeadStatuses as removeOrgDefaultLeadStatusesMutation,
} from '@/graphql/mutations'
import { parseAwsJson } from '@/utils/graphql'
import { getApiMessage } from '@/mixins/Extras/ApiResponse'

const client = generateClient()

// Same EMAIL/TIME gap as contextsApi.js's own CONTEXT_FIELD_TYPES -- see
// its comment for the full story.
const FIELD_TYPES = new Set([
  'TEXT',
  'NUMBER',
  'DROPDOWN',
  'PHONE_NUMBER',
  'DATE',
  'TIME',
  'EMAIL',
])

async function runOrgDefaultsField(document, field, input) {
  try {
    const { data, errors } = await client.graphql({
      query: document,
      variables: { input },
    })

    if (errors?.length) {
      throw new Error(errors[0].message)
    }

    return parseAwsJson(data?.[field])
  } catch (error) {
    return {
      status: 'ERROR',
      message: getApiMessage(error, 'Request failed'),
    }
  }
}

function cleanStatusList(value) {
  return (Array.isArray(value) ? value : [value])
    .map((status) => String(status ?? '').trim().toUpperCase().replace(/\s+/g, '_'))
    .filter(Boolean)
}

/**
 * Org-level default field definitions — seed the custom_field_definitions
 * of every NEWLY created context (never retroactive). Same shape/rules as
 * addContextFields: label mandatory, key optional (server slugifies label),
 * options required for DROPDOWN, single unique-identifier flag.
 */
export function addOrgDefaultFields(input = {}) {
  const definitions = (Array.isArray(input.field_definitions)
    ? input.field_definitions
    : []
  )
    .map((definition) => {
      const label = String(definition?.label ?? definition?.name ?? '').trim()
      const key = String(definition?.key ?? '').trim()
      const type = String(definition?.type ?? '').trim().toUpperCase()
      if (!label || !FIELD_TYPES.has(type)) return null

      const entry = { label, type }
      if (key) entry.key = key
      if (type === 'DROPDOWN') {
        const options = (Array.isArray(definition?.options)
          ? definition.options
          : []
        )
          .map((option) => String(option ?? '').trim())
          .filter(Boolean)
        if (!options.length) return null
        entry.options = options
      }
      if (definition?.enabled != null) entry.enabled = Boolean(definition.enabled)
      if (definition?.is_unique_identifier != null) {
        entry.is_unique_identifier = Boolean(definition.is_unique_identifier)
      }
      return entry
    })
    .filter(Boolean)

  if (!definitions.length) {
    return Promise.resolve({
      status: 'ERROR',
      message: 'At least one valid field definition is required.',
    })
  }

  return runOrgDefaultsField(addOrgDefaultFieldsMutation, 'addOrgDefaultFields', {
    field_definitions: definitions,
  })
}

/**
 * Remove org default field definition(s) by key. Blocks removing the field
 * currently flagged as the default identifier; there is no "in use by
 * leads" check since defaults only apply at context creation.
 */
export function removeOrgDefaultFields(input = {}) {
  const fieldNames = (Array.isArray(input.field_names)
    ? input.field_names
    : [input.field_names]
  )
    .map((name) => String(name ?? '').trim())
    .filter(Boolean)

  if (!fieldNames.length) {
    return Promise.resolve({
      status: 'ERROR',
      message: 'At least one field name is required.',
    })
  }

  return runOrgDefaultsField(
    removeOrgDefaultFieldsMutation,
    'removeOrgDefaultFields',
    { field_names: fieldNames },
  )
}

/** Add status value(s) to the org's default lead-status list (dedupes). */
export function addOrgDefaultLeadStatuses(input = {}) {
  const statuses = cleanStatusList(input.statuses)
  if (!statuses.length) {
    return Promise.resolve({
      status: 'ERROR',
      message: 'At least one status is required.',
    })
  }

  return runOrgDefaultsField(
    addOrgDefaultLeadStatusesMutation,
    'addOrgDefaultLeadStatuses',
    { statuses },
  )
}

/**
 * Remove status value(s) from the org's default list. The server blocks
 * emptying the list ("at least one must remain"); no in-use check applies.
 */
export function removeOrgDefaultLeadStatuses(input = {}) {
  const statuses = cleanStatusList(input.statuses)
  if (!statuses.length) {
    return Promise.resolve({
      status: 'ERROR',
      message: 'At least one status is required.',
    })
  }

  return runOrgDefaultsField(
    removeOrgDefaultLeadStatusesMutation,
    'removeOrgDefaultLeadStatuses',
    { statuses },
  )
}
